import { useEffect, useState } from "react";
import axios from "axios";
import { Phone, Mail } from "lucide-react";
import { apiImg, telHref } from "@/lib/siteSettings";
import { Reveal, SectionHeading } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Team = () => {
  const [members, setMembers] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/team`)
      .then(({ data }) => setMembers(data))
      .catch(() => setMembers([]));
  }, []);

  if (!members || members.length === 0) return null;

  return (
    <section id="team" className="bg-warm py-16 sm:py-24 lg:py-28" data-testid="team-section">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          center
          label="Our Team"
          title="Our Management Team"
          subtitle="The people who guide your property journey, every step of the way."
        />

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 lg:gap-8">
          {members.map((m, i) => (
            <Reveal key={m.id} delay={(i % 4) * 0.08}>
              <article
                className="group bg-white rounded-xl border border-line overflow-hidden hover:shadow-[0_24px_60px_rgba(11,31,51,0.12)] hover:-translate-y-1.5 transition-all duration-400 h-full"
                data-testid={`team-card-${m.id}`}
              >
                <div className="relative aspect-[4/4.4] overflow-hidden bg-line/40">
                  {m.photo ? (
                    <img
                      src={apiImg(m.photo)}
                      alt={m.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-display text-5xl font-semibold text-gold/60">
                      {m.name.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="font-display text-lg sm:text-xl font-semibold text-navy leading-snug">{m.name}</h3>
                  {m.designation && (
                    <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.18em] text-gold">{m.designation}</p>
                  )}
                  {m.bio && <p className="mt-3 text-[13px] text-ink-soft leading-relaxed line-clamp-3">{m.bio}</p>}
                  {(m.phone || m.email) && (
                    <div className="mt-4 pt-4 border-t border-line flex gap-2.5">
                      {m.phone && (
                        <a
                          href={telHref(m.phone)}
                          aria-label={`Call ${m.name}`}
                          data-testid={`team-call-${m.id}`}
                          className="h-9 w-9 rounded-lg border border-line flex items-center justify-center text-navy hover:border-gold hover:text-gold transition-colors"
                        >
                          <Phone size={14} />
                        </a>
                      )}
                      {m.email && (
                        <a
                          href={`mailto:${m.email}`}
                          aria-label={`Email ${m.name}`}
                          data-testid={`team-email-${m.id}`}
                          className="h-9 w-9 rounded-lg border border-line flex items-center justify-center text-navy hover:border-gold hover:text-gold transition-colors"
                        >
                          <Mail size={14} />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
